import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, MotionConfig } from "framer-motion";
import { FONT } from "@/lib/tokens";
import { appleEase } from "@/lib/motion";
import SystemWindow from "@/components/home/SystemWindow";

/* ──────────────────────────────────────────────────────────────
   Comment ca marche.

   Reproche des premiers relecteurs externes : "on demande un effort
   de projection enorme pour comprendre ce qu'on recoit apres avoir
   clique sur Acheter". Les exemples montrent ce qu'un systeme FAIT ;
   il manquait ce qui se passe quand on en prend un.

   Les trois blocs disent le parcours reel, pas une promesse : ce
   qu'une fiche annonce avant paiement (les onze declarations du
   Standard), ce qui arrive a l'achat, ce que l'installation demande.
   Le chapeau ne parle plus d'"etapes" depuis que les titres sont des
   qualites et non des actions : il nomme la trajectoire, fiche ->
   systeme en marche.
   Aucune duree ni aucun chiffre ici : ils dependent du systeme et
   sont annonces fiche par fiche. On n'invente pas de chiffre.

   La fenetre d'exemple, qui avait sa propre section ("Qu'est-ce
   qu'un Systeme TANGIG ?"), a ete absorbee ici : le visiteur voit
   l'objet AVANT qu'on lui explique ce qui se passe quand il en
   prend un. Trois definitions se suivaient sur l'accueil ; il en
   reste deux, et celle-ci sert d'amorce a la scene.

   Forme : trois cartes cernees d'un trait noir, sans fond, sur le
   gris de la section. Plus de filet de separation, plus de couleur
   sur les titres : tout le dessin tient dans le bord des cartes.
   En mobile la rangee defile horizontalement et des puces disent ou
   l'on se trouve. La liste qui defile est enfant DIRECT de la
   colonne de section, sinon elle ne defile pas.
   ────────────────────────────────────────────────────────────── */

const INK    = "#1D1D1F";
const GREY_1 = "#6E6E73";
/* Le trait des cartes est un noir pur assume, pas une nuance de gris. */
const BORDER = "#000000";
/* Le bleu de marque, reserve ici a la derniere ligne du titre. */
const BLUE   = "#1D5FD1";

const STEPS = [
  {
    title: "Transparence totale",
    text: "Chaque fiche indique le résultat visé, le contenu, le temps de mise en place, les prérequis et les outils. Tout est clair avant d'acheter.",
  },
  {
    title: "Système complet, pas une théorie",
    text: "Vous recevez tout ce qu'il faut : procédures, gabarits, automatisations et suivi. Ni formation ni simple PDF, mais une infrastructure prête à tourner.",
  },
  {
    title: "Déploiement autonome",
    text: "Des étapes pas à pas pour une mise en route immédiate. Une fois installé, le système fonctionne sans dépendre constamment de son créateur.",
  },
];

const CSS = `
  .tg-how-section {
    background: #F5F5F7;
    padding: 120px clamp(24px, 5.5vw, 80px) 128px;
    font-family: ${FONT};
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .tg-how-header { text-align: center; max-width: 760px; margin: 0 auto 72px; }

  /* Meme token de label que les autres sections de l'accueil. */
  .tg-how-eyebrow {
    font-size: clamp(18px, 2vw, 19.5px);
    font-weight: 600;
    letter-spacing: -0.01em;
    color: #3A3A3C;
    margin: 0 0 20px;
  }

  .tg-how-title {
    font-size: clamp(30px, 4.6vw, 44px);
    font-weight: 700;
    letter-spacing: -0.03em;
    line-height: 1.15;
    color: ${INK};
    margin: 0 0 24px;
  }

  /* Les trois temps sont empiles : une ligne par etape, jamais deux
     phrases sur la meme ligne, quelle que soit la largeur. Les deux
     premiers temps restent noirs, le bleu ne tombe que sur le resultat,
     qui est le seul mot qui compte vraiment. */
  .tg-how-title-line { display: block; }
  .tg-how-title-line-2 { color: ${INK}; }
  .tg-how-title-line-3 { color: ${BLUE}; }

  /* La definition d'un systeme, recuperee de l'ancienne section. */
  .tg-how-lead {
    font-size: 18px;
    font-weight: 600;
    line-height: 1.6;
    letter-spacing: -0.01em;
    color: ${GREY_1};
    margin: 0;
  }

  .tg-how-window {
    width: min(1180px, 100%);
    margin: 0 0 clamp(72px, 7vw, 104px);
  }

  .tg-how-grid {
    width: min(1120px, 100%);
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: clamp(16px, 1.7vw, 24px);
  }

  .tg-how-step {
    /* Pas de fond : sur le gris de la section, le trait noir suffit a
       dessiner la carte. Un blanc en plus ferait double signal. */
    background: transparent;
    border: 1px solid ${BORDER};
    border-radius: 20px;
    padding: clamp(24px, 2.3vw, 34px);
  }

  .tg-how-step-title {
    font-size: clamp(19px, 1.8vw, 22px);
    font-weight: 700;
    letter-spacing: -0.02em;
    line-height: 1.3;
    color: ${INK};
    margin: 0 0 12px;
  }

  .tg-how-step-text {
    font-size: 16px;
    line-height: 1.6;
    letter-spacing: -0.01em;
    color: ${GREY_1};
    margin: 0;
  }

  /* Les puces n'existent qu'en mobile, ou la rangee defile. */
  .tg-how-dots { display: none; }

  .tg-how-note {
    margin: 60px 0 0;
    font-size: 15.5px;
    line-height: 1.55;
    font-weight: 600;
    color: #86868B;
    text-align: center;
  }

  .tg-how-note a {
    color: ${INK};
    font-weight: 600;
    text-decoration: none;
    border-bottom: 1px solid rgba(0,0,0,0.22);
    padding-bottom: 1px;
    transition: border-color 0.2s ease;
  }
  .tg-how-note a:hover { border-bottom-color: ${INK}; }

  @media (max-width: 767px) {
    .tg-how-section { padding: 80px 0 84px; }
    .tg-how-header  { margin-bottom: 44px; padding: 0 24px; }
    .tg-how-title   { font-size: 29px; margin-bottom: 18px; }
    .tg-how-lead    { font-size: 16px; }

    /* La fenetre reprend une marge laterale : le rail des cartes est le
       seul element qui va bord a bord. */
    .tg-how-window {
      align-self: stretch;
      width: auto;
      margin: 0 20px clamp(48px, 9vw, 64px);
    }

    /* Defilement horizontal bord a bord : la rangee sort du padding de
       la section pour que la carte suivante se devine au bord droit. */
    .tg-how-grid {
      align-self: stretch;
      width: auto;
      display: flex;
      gap: 14px;
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
      scroll-snap-type: x mandatory;
      scroll-padding-left: 24px;
      padding: 4px 24px;
      scrollbar-width: none;
    }
    .tg-how-grid::-webkit-scrollbar { display: none; }

    .tg-how-step {
      flex: 0 0 79%;
      scroll-snap-align: start;
      padding: 26px 24px 28px;
    }

    .tg-how-dots {
      display: flex;
      justify-content: center;
      gap: 8px;
      margin: 22px 0 0;
      padding: 0;
    }
    /* Exactement les puces de la section "Entreprise ou createur" : le
       point actif s'allonge en trait. Meme taille, memes gris, meme
       transition — deux carrousels sur la meme page ne peuvent pas avoir
       deux paginations differentes. */
    .tg-how-dot {
      width: 7px;
      height: 7px;
      padding: 0;
      border: none;
      border-radius: 999px;
      background: #C7C7CC;
      cursor: pointer;
      transition: background 0.25s ease, width 0.25s ease;
      -webkit-appearance: none;
      appearance: none;
    }
    .tg-how-dot[data-active="true"] { width: 22px; background: ${INK}; }

    .tg-how-note { margin-top: 40px; padding: 0 24px; }
  }
`;

export default function HowItWorksSection() {
  const railRef = useRef(null);
  const [active, setActive] = useState(0);

  /* La puce active se deduit de la position de defilement : un seul
     listener, pas d'observateur par carte. */
  const onScroll = useCallback(() => {
    const rail = railRef.current;
    if (!rail) return;
    const card = rail.firstElementChild;
    if (!card) return;
    const step = card.getBoundingClientRect().width + 14;
    const i = Math.round(rail.scrollLeft / step);
    setActive(Math.max(0, Math.min(STEPS.length - 1, i)));
  }, []);

  useEffect(() => {
    const rail = railRef.current;
    if (!rail) return undefined;
    rail.addEventListener("scroll", onScroll, { passive: true });
    return () => rail.removeEventListener("scroll", onScroll);
  }, [onScroll]);

  const goTo = (i) => {
    const rail = railRef.current;
    const card = rail?.children[i];
    if (!rail || !card) return;
    rail.scrollTo({ left: card.offsetLeft - rail.offsetLeft, behavior: "smooth" });
  };

  const reveal = {
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.25 },
    transition: { duration: 0.8, ease: appleEase },
  };

  return (
    <MotionConfig reducedMotion="user">
      <style>{CSS}</style>
      <section className="tg-how-section">
        <div className="tg-how-header">
          <motion.p className="tg-how-eyebrow" {...reveal}>
            Comment ça marche
          </motion.p>
          <motion.h2
            className="tg-how-title"
            {...reveal}
            transition={{ ...reveal.transition, delay: 0.08 }}
          >
            <span className="tg-how-title-line">Vous choisissez.</span>
            <span className="tg-how-title-line tg-how-title-line-2">Vous installez.</span>
            <span className="tg-how-title-line tg-how-title-line-3">Le système tourne.</span>
          </motion.h2>
          <motion.p
            className="tg-how-lead"
            {...reveal}
            transition={{ ...reveal.transition, delay: 0.16 }}
          >
            Un Système TANGIG est un ensemble cohérent de composants (logiciels, automatisations, IA,
            documentation et ressources) conçu pour résoudre un besoin métier et être déployé rapidement
            par une entreprise ou un professionnel.
          </motion.p>
        </div>

        <div className="tg-how-window">
          <SystemWindow />
        </div>

        <div className="tg-how-grid" ref={railRef}>
          {STEPS.map((s, i) => (
            <motion.div
              key={s.title}
              className="tg-how-step"
              {...reveal}
              transition={{ ...reveal.transition, delay: 0.08 * i }}
            >
              <h3 className="tg-how-step-title">{s.title}</h3>
              <p className="tg-how-step-text">{s.text}</p>
            </motion.div>
          ))}
        </div>

        <div className="tg-how-dots">
          {STEPS.map((s, i) => (
            <button
              key={s.title}
              type="button"
              className="tg-how-dot"
              data-active={i === active}
              aria-label={s.title}
              onClick={() => goTo(i)}
            />
          ))}
        </div>

        <motion.p
          className="tg-how-note"
          {...reveal}
          transition={{ ...reveal.transition, delay: 0.3 }}
        >
          Ce que TANGIG exige de chaque système avant publication&nbsp;:{" "}
          <Link to="/le-standard">Le Standard</Link>
        </motion.p>
      </section>
    </MotionConfig>
  );
}
